import * as React from 'react';
import { cn } from '@/util/cn';

// Row-as-card primitive. Pairs with `common/DataTable` on surfaces
// where the horizontal-scroll fallback isn't great on narrow widths —
// the page renders a DataTable above md and a stack of MobileDataCards
// below md. Each card opens with the row's identifier (Resort name
// + link, typically) as a bold heading, and lists each remaining
// column as a labeled stat row.
//
// Group cards inside a bordered container so the row separators read
// as a single component:
//
//   <div className="md:hidden">
//     <SectionCard as="div" padding="none" className="overflow-hidden">
//       {rows.map((r) => (
//         <MobileDataCard
//           key={r.uuid}
//           heading={<Link href={...}>{r.title}</Link>}
//         >
//           <MobileDataCard.Stat label="Base" value={dt(r.snow.base)} />
//           <MobileDataCard.Stat label="Lifts" value={`${open} / ${total}`} />
//         </MobileDataCard>
//       ))}
//     </div>
//   </div>
//
// Stats render in DOM order via a <dl> — pass them in the same order
// the desktop columns appear so scanning down a card matches scanning
// across the table.

export type MobileDataCardLayout = 'rows' | 'grid';

const LayoutContext = React.createContext<MobileDataCardLayout>('rows');

export type MobileDataCardProps = {
  /** Primary label for the card. Typically the row's identifier
   *  (a resort name + link). */
  heading?: React.ReactNode;
  /** `MobileDataCard.Stat` children for the labelled stat list. */
  children?: React.ReactNode;
  /** "rows" (default) lays stats out as full-width label / value
   *  pairs. "grid" packs them into a 3-column micro-grid with the
   *  label stacked above each value — useful when a card carries 4+
   *  small numeric stats and the rows layout would read as too tall. */
  layout?: MobileDataCardLayout;
  className?: string;
};

const Root = ({
  heading, children, layout = 'rows', className,
}: MobileDataCardProps) => (
  <div className={cn('border-b border-border px-4 py-4 last:border-b-0', className)}>
    {heading && (
      <div className="mb-3 text-base font-semibold text-foreground">{heading}</div>
    )}
    {children && (
      <LayoutContext.Provider value={layout}>
        {layout === 'grid' ? (
          <dl className="grid grid-cols-3 gap-x-3 gap-y-3">{children}</dl>
        ) : (
          <dl className="space-y-1.5">{children}</dl>
        )}
      </LayoutContext.Provider>
    )}
  </div>
);

export type MobileDataCardStatProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
};

const Stat = ({ label, value, className }: MobileDataCardStatProps) => {
  const layout = React.useContext(LayoutContext);
  if (layout === 'grid') {
    return (
      <div className={cn('flex flex-col gap-0.5', className)}>
        <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="text-sm font-semibold tabular-nums text-foreground">
          {value}
        </dd>
      </div>
    );
  }
  return (
    <div className={cn('flex items-baseline justify-between gap-3', className)}>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
};

export const MobileDataCard = Object.assign(Root, { Stat });
